<template>
  <div>
    <v-card-title v-if="!cardOnly">
      <v-row>
        <v-col class="d-flex flex-column align-start ml-2" sm="6" md="6" v-if="!token">
          <v-btn fab dark small elevation="0" class="pa-2 mb-1" @click="previousSlide">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          <span>Back</span>
        </v-col>
      </v-row>
    </v-card-title>
    <h2 v-if="loading">Loading your virtual card...</h2>
    <p v-if="loading" class="grey--text mt-2">This should only take a few seconds.</p>
    <Robin
      v-if="loading"
      :showText="false"
      :width="140"
      :height="140"
      animation="babyDance"
      :loop="true"
      class="d-flex justify-center"
    />
    <h1 v-if="!loading && !cardOnly">Your Virtual Card</h1>
    <div id="marqeta-script"></div>
    <div id="marqeta-bootstrap-script"></div>
    <Alert
      v-if="loadFailed"
      variant="error"
      :message="loadFailedMessage"
      data-testid="virtual-card-error"
    />
    <v-card
      v-else
      class="mx-auto align-text-left py-2 px-2 virtual-card"
      :elevation="elevation ? 2 : 0"
      :color="card.backgroundColor"
      :class="{
        'hidden shrink': loading,
        'my-4': !cardOnly,
        'card-border rounded-xl': !elevation,
        'virtual-card--background-image': !!card.backgroundImageUrl,
      }"
      :style="cardStyle"
    >
      <div
        class="d-flex flex-column px-xs-6"
        :class="{
          'brand--text': !card.dark,
          'white--text': card.dark,
        }"
      >
        <div class="card-header pr-3" :class="{ 'card-header--logo-left': !logoRight }">
          <p class="customer-service-text card-header__service mb-1 text-right">
            Customer service
            <span class="customer-service-url">{{ card.customerServiceUrl }}</span>
          </p>
          <img
            :src="logoUrl"
            :style="logoStyle"
            alt="Logo"
            class="card-logo mb-1"
            data-testid="card-logo"
            @error="onLogoError"
          />
          <p class="virtual-card-text card-header__virtual"><b>Virtual Card</b></p>
        </div>
        <p class="label-text mb-0"><b>CARDHOLDER NAME</b></p>
        <p class="card-text mb-1">
          <b>{{ name ? name : "Cardholder Name" }}</b>
        </p>
        <p class="label-text mb-0"><b>ACCOUNT NUMBER</b></p>
        <div class="pci-box d-flex">
          <div id="mq-card-pan" class="card-pan"></div>
          <div id="copy-pan-container" style="position: relative">
            <v-icon alt="Copy card number" :color="card.dark ? 'white' : undefined"
              >mdi-content-copy</v-icon
            >
          </div>
        </div>
        <div class="d-flex">
          <div class="d-flex flex-column">
            <p class="label-text mb-0"><b>CVV2</b></p>
            <div class="pci-box-small d-flex">
              <div id="mq-card-cvv" class="card-cvv"></div>
              <div id="copy-cvv-container" style="position: relative">
                <v-icon alt="Copy CVV2" :color="card.dark ? 'white' : undefined"
                  >mdi-content-copy</v-icon
                >
              </div>
            </div>
          </div>
          <div class="d-flex flex-column ml-4">
            <p class="label-text mb-0"><b>Valid Thru</b></p>
            <div class="pci-box-small d-flex">
              <div id="mq-card-exp" class="card-exp"></div>
              <div id="copy-exp-container" style="position: relative">
                <v-icon alt="Copy expiration date" :color="card.dark ? 'white' : undefined"
                  >mdi-content-copy</v-icon
                >
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        class="d-flex justify-space-between full-width px-1"
        :class="{
          'brand--text': !card.dark,
          'white--text': card.dark,
        }"
      >
        <div class="d-flex flex-column justify-space-between full-width">
          <p class="label-text mb-1 ml-4"><b>LIMITED USE</b></p>
          <p class="fdic-text mb-1 mt-1 word-break">
            <b>{{ card.issuerStatement }}</b>
          </p>
        </div>
        <div class="d-flex flex-column align-end">
          <p class="virtual-card-text mr-2 mb-1"><b>DEBIT</b></p>
          <v-img :src="card.visaLogoUrl" max-width="105" contain data-testid="visa-logo" />
        </div>
      </div>
    </v-card>
    <body></body>
  </div>
</template>

<script>
import MarqetaService from "@/services/MarqetaService";
import Robin from "@/components/Robin";
import Alert from "@/components/generic/Alert.vue";
import { DEFAULT_CARD_BRANDING, resolveCardBranding } from "@/utils/cardProduct";
import postscribe from "postscribe";
import moment from "moment";
import { mapState } from "vuex";

const LOAD_FAILED_MESSAGE =
  "We couldn't load your virtual card. Please try again later or contact support if the problem persists.";

export default {
  name: "MarqetaDisplayCard",
  props: {
    token: String,
    cardOnly: Boolean,
    elevation: {
      type: Boolean,
      default: true,
    },
    magicLink: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    Robin,
    Alert,
  },
  data() {
    return {
      loading: false,
      loadFailed: false,
      loadFailedMessage: LOAD_FAILED_MESSAGE,
      logoFailed: false,
      name: null,
      displaySmallVersion: false,
      branding: null,
    };
  },
  created() {
    this.displaySmallVersion = !!(window.innerWidth < 1200);
    this.getWidgetData();
    this.getMarqetaUser();
  },
  mounted() {
    if (process.env.VUE_APP_ENVIRONMENT == "dev" || process.env.VUE_APP_ENVIRONMENT == "test") {
      var marqetaUrl = "https://widgets-sandbox.marqeta.com/marqetajs/1.1.0/marqeta.min.js";
    } else {
      marqetaUrl = "https://widgets.marqeta.com/marqetajs/1.1.0/marqeta.min.js";
    }
    postscribe(
      "#marqeta-script",
      `<script src="${marqetaUrl}" type="text/javascript"><\/script>` //eslint-disable-line
    );
  },
  beforeUnmount() {
    this.isDestroyed = true;
  },
  methods: {
    getWidgetData() {
      var startTime = new Date();
      this.loading = true;
      const cardFontSize = this.displaySmallVersion ? "18px" : "1.4em";
      MarqetaService.getVirtualCardWidget(
        this.token,
        this.magicLink ? this.token : null,
        this.magicLinkToken
      )
        .then((x) => {
          if (this.isDestroyed) return;
          if (!x || !x.client_access_token) {
            console.log("Virtual card widget response had no client access token");
            this.failWidgetLoad();
            return;
          }
          this.branding = x.branding || null;
          const textColor = this.card.dark ? "white" : "#1E2549";
          postscribe(
            "#marqeta-bootstrap-script",
            `<script>if (document.getElementById("mq-card-pan")) marqeta.bootstrap({
            "clientAccessToken": "${x.client_access_token}",
            "showPan": {
              "cardPan": {
                "domId": "mq-card-pan",
                "format": true,
                "styles": { span: {"font-family": "Avenir", "font-size": "${cardFontSize}", "color": "${textColor}", "font-weight": "500", "white-space": "nowrap"} }  // A custom styles object
              },
              "copyCardPan": {
                "domId": "copy-pan-container",
                "mode": "transparent",
                "onCopySuccess": () => { console.log("Copied PAN!"); },
                "onCopyFailure": error => {
                  console.error(error);
                }
              },
              "cardExp": {
                "domId": "mq-card-exp",
                "format": true,
                "styles": { span: {"font-family": "Avenir", "font-size": "${cardFontSize}", "color": "${textColor}", "font-weight": "500"} }
              },
              "copyCardExp": {
                "domId": "copy-exp-container",
                "mode": "transparent",
                "onCopySuccess": () => { console.log("Copied expiration date!"); },
                "onCopyFailure": error => {
                  console.error(error);
                }
              },
              "cardCvv": {
                "domId": "mq-card-cvv",
                "styles": { span: {"font-family": "Avenir", "font-size": "${cardFontSize}", "color": "${textColor}", "font-weight": "500"} }
              },
              "copyCardCvv": {
                "domId": "copy-cvv-container",
                "mode": "transparent",
                "onCopySuccess": () => { console.log("Copied CVV2!"); },
                "onCopyFailure": error => {
                  console.error(error);
                }
              }
            },
            "callbackEvents": {
              "onSuccess": () => { console.log("Success!")
              },
              "onFailure": error => { console.error(error);
              }
            }
          }
          );<\/script>` //eslint-disable-line
          );

          var timeDiff = moment().diff(moment(startTime));
          if (timeDiff < 2000) {
            setTimeout(() => {
              this.loading = false;
            }, 2000 - timeDiff);
          } else {
            this.loading = false;
          }
        })
        .catch((error) => {
          console.log("There was an error getting the virtual card widget: ", error);
          if (this.isDestroyed) return;
          this.failWidgetLoad();
        });
    },
    failWidgetLoad() {
      this.loadFailed = true;
      this.loading = false;
    },
    onLogoError() {
      this.logoFailed = true;
    },
    getMarqetaUser() {
      console.log("Getting user info");
      MarqetaService.getUser(this.token, this.magicLink ? this.token : null, this.magicLinkToken)
        .then((user) => {
          console.log("User response: ", user);
          this.name = (user.first_name + " " + user.last_name).toUpperCase();
        })
        .catch((error) => {
          console.log("There was an error getting user: ", error);
          // this.currentSlide++;
        });
    },
    resetForm() {
      this.$emit("scrollToTop");
      this.$emit("reset");
      this.$emit("get-cards", "marqetaDisplayCard");
    },
    previousSlide() {
      this.$emit("scrollToTop");
      this.resetForm();
    },
  },
  computed: {
    ...mapState(["magicLinkToken"]),
    card() {
      return resolveCardBranding(this.branding);
    },
    logoUrl() {
      return this.logoFailed ? DEFAULT_CARD_BRANDING.logoUrl : this.card.logoUrl;
    },
    logoRight() {
      return this.card.logoPlacement === "RIGHT";
    },
    logoStyle() {
      return { maxWidth: `${this.card.logoMaxWidth}px` };
    },
    cardStyle() {
      const url = this.card.backgroundImageUrl;
      return url ? { backgroundImage: `url("${url}")` } : {};
    },
  },
  watch: {},
};
</script>

<style scoped id="styles">
/* A fixed width, not only a max: the wallet panel wraps the card in an inline-block that shrinks to its content,
   so a short issuer statement would otherwise narrow the whole card. */
.virtual-card {
  width: 485px;
  max-width: 100%;
  min-width: 0;
}

.virtual-card--background-image {
  background-repeat: no-repeat;
  background-position: left center;
  /* Full card height; width follows image aspect ratio (does not stretch full width) */
  background-size: auto 100%;
}

.virtual-card.shrink {
  height: 1px;
}
/* Code for transitions between slides */

.topic-left-enter {
  opacity: 0;
  transform: translateX(100%);
}

.topic-right-enter {
  opacity: 0;
  transform: translateX(-100%);
}

.topic-left-enter-active,
.topic-right-enter-active {
  transition: all 0.65s cubic-bezier(0.19, 1, 0.22, 1);
}

#copy-pan-container:hover > .v-icon {
  opacity: 0.5;
  cursor: pointer;
}

#copy-exp-container:hover > .v-icon {
  opacity: 0.5;
  cursor: pointer;
}

#copy-cvv-container:hover > .v-icon {
  opacity: 0.5;
  cursor: pointer;
}

.pci-box {
  height: 35px;
}
.pci-box-small {
  height: 25px;
}

.card-header {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  justify-items: end;
}
.card-header--logo-left {
  grid-template-columns: minmax(0, 1fr) auto;
  grid-template-areas:
    "service service"
    "logo virtual";
  align-items: center;
}
.card-header--logo-left .card-logo {
  grid-area: logo;
  justify-self: stretch;
}
.card-header--logo-left .card-header__service {
  grid-area: service;
}
.card-header--logo-left .card-header__virtual {
  grid-area: virtual;
  align-self: start;
}
/* A native img, not v-img: v-img keeps the first image's aspect ratio when src changes, and the default logo shows
   before branding arrives, so a cobrand logo was squeezed into the Whistle logo's proportions. */
.card-logo {
  min-width: 0;
  width: 100%;
  height: auto;
}
.customer-service-text {
  overflow-wrap: break-word;
}
.customer-service-url {
  display: inline-block;
  max-width: 100%;
}

.card-pan {
  width: 235px;
  min-width: 235px;
}
.pci-box >>> iframe,
.pci-box-small >>> iframe {
  max-width: 100%;
}
.card-exp {
  width: 75px;
}
.card-cvv {
  width: 55px;
}

.card-text {
  font-size: 1.35em;
}

.virtual-card-text {
  font-size: 1.3em;
}
.customer-service-text {
  font-size: 12px;
  font-weight: 600;
}
.label-text {
  font-size: 12px;
}
.fdic-text {
  font-size: 10px;
  line-height: 120%;
}

.card-border {
  border: solid 1px !important;
  border-color: lightgray !important;
  /* border-radius: 0; */
}

@media (max-width: 1200px) {
  .card-text {
    font-size: 18px;
  }
  .virtual-card-text {
    font-size: 16px;
  }
  .card-pan {
    width: 200px;
    min-width: 192px;
  }
  .card-exp {
    width: 65px;
  }
  .card-cvv {
    width: 45px;
  }
}
</style>
